// AI Configuration - calls serverless functions (no API keys in browser)
// Model is hardcoded to Claude Sonnet 4.6 on the backend - no user selection needed

import { apiFetch } from './api';

// Resolve any legacy model names to a consistent value (backend maps all to claude-opus-4-6)
export function resolveModel(model: string): string {
  return model || 'claude-opus-4-6';
}

// Helper to read an SSE stream and accumulate all text chunks
async function readSSEStream(response: Response): Promise<string> {
  const contentType = response.headers.get('Content-Type') || '';

  if (!contentType.includes('text/event-stream')) {
    // Fallback for non-streaming responses
    const data = await response.json();
    return data.outline || data.brief || data.validation || data.content || '';
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let accumulated = '';
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ') && line !== 'data: [DONE]') {
        try {
          const parsed = JSON.parse(line.slice(6));
          if (parsed.text) {
            accumulated += parsed.text;
          }
          if (parsed.error) {
            throw new Error(parsed.error);
          }
        } catch (e: any) {
          if (e.message && !e.message.includes('JSON')) {
            throw e;
          }
        }
      }
    }
  }

  return accumulated;
}

// Streaming variant that calls onChunk for each text delta
async function readSSEStreamWithCallback(
  response: Response,
  onChunk: (accumulated: string) => void
): Promise<string> {
  const contentType = response.headers.get('Content-Type') || '';

  if (!contentType.includes('text/event-stream')) {
    const data = await response.json();
    const text = data.outline || data.brief || data.validation || data.content || '';
    onChunk(text);
    return text;
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let accumulated = '';
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ') && line !== 'data: [DONE]') {
        try {
          const parsed = JSON.parse(line.slice(6));
          if (parsed.text) {
            accumulated += parsed.text;
            onChunk(accumulated);
          }
          if (parsed.error) {
            throw new Error(parsed.error);
          }
        } catch (e: any) {
          if (e.message && !e.message.includes('JSON')) {
            throw e;
          }
        }
      }
    }
  }

  return accumulated;
}

export async function generateChapterContent(params: {
  model: string;
  bookTitle: string;
  audience: string;
  perspective: string;
  goals: string;
  disclaimers?: string;
  rules: string;
  writingStyleSample?: string;
  styleNotes?: string;
  preferredTone: string[];
  chapterTitle: string;
  chapterType:
    | 'introduction'
    | 'chapter'
    | 'conclusion'
    | 'glossary'
    | 'checklist'
    | 'appendix'
    | 'bonus'
    | 'faq';
  chapterNotes: string;
  target_word_count?: number;
  bookType?: string;
  researchContent?: string;
  priorChapterOpenings?: string[];
  chapterBrief?: string;
  citationsEnabled?: boolean;
  manualReferences?: string;
  chapterReferences?: string;
}): Promise<string> {
  const resolvedModel = resolveModel(params.model);

  const response = await apiFetch('/api/generate-chapter', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...params,
      model: resolvedModel,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to generate content (${response.status})`);
  }

  const content = await readSSEStream(response);

  if (!content) {
    throw new Error('No content was generated');
  }

  // Check word count — if under 85% of target, call expand-chapter endpoint
  // Up to 2 expansion attempts allowed.
  const targetWordCount = params.target_word_count || 1000;

  // Calculate the same adjusted target the backend uses
  let adjustedTarget = targetWordCount;
  if (params.chapterType === 'introduction') {
    adjustedTarget = Math.floor(targetWordCount * 0.75);
  } else if (params.chapterType === 'conclusion') {
    adjustedTarget = Math.floor(targetWordCount * 0.5);
  } else if (['glossary', 'checklist', 'appendix', 'bonus', 'faq'].includes(params.chapterType)) {
    adjustedTarget = Math.floor(targetWordCount * 0.6);
  }
  const promptTarget = adjustedTarget;

  let currentContent = content;
  const MAX_EXPANSIONS = 2;

  for (let attempt = 1; attempt <= MAX_EXPANSIONS; attempt++) {
    const currentWords = currentContent.trim().split(/\s+/).length;
    const pct = Math.round(currentWords / adjustedTarget * 100);

    if (currentWords >= adjustedTarget * 0.80) {
      console.log(
        `Chapter "${params.chapterTitle}" is ${currentWords}/${adjustedTarget} words (${pct}%) — meets target.`
      );
      break;
    }

    console.log(
      `Chapter "${params.chapterTitle}" is ${currentWords}/${adjustedTarget} words (${pct}%), expansion attempt ${attempt}/${MAX_EXPANSIONS}...`
    );

    try {
      // Delay before first attempt to avoid Netlify cold start failures
      if (attempt === 1) {
        console.log(`[expand-chapter] Waiting 2s before first expansion call (cold start mitigation)...`);
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      console.log(
        `[expand-chapter] Starting expansion attempt ${attempt} for "${params.chapterTitle}" — current: ${currentWords} words, target: ${adjustedTarget}, deficit: ${adjustedTarget - currentWords} words`
      );

      const expandResponse = await apiFetch('/api/expand-chapter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft: currentContent,
          targetWordCount: adjustedTarget,
          promptWordCount: promptTarget,
          currentWordCount: currentWords,
          bookTitle: params.bookTitle,
          chapterTitle: params.chapterTitle,
          chapterType: params.chapterType,
        }),
      });

      if (!expandResponse.ok) {
        const errBody = await expandResponse.text().catch(() => 'no body');
        console.error(
          `[expand-chapter] Attempt ${attempt} FAILED for "${params.chapterTitle}": HTTP ${expandResponse.status} — ${errBody}`
        );
        break;
      }

      const expanded = await readSSEStream(expandResponse);
      const expandedWords = expanded ? expanded.trim().split(/\s+/).length : 0;

      if (!expanded) {
        console.error(
          `[expand-chapter] Attempt ${attempt} returned EMPTY response for "${params.chapterTitle}" — stream may have timed out or errored. Response content-type: ${expandResponse.headers.get('Content-Type')}`
        );
        break;
      }

      if (expandedWords <= currentWords) {
        console.error(
          `[expand-chapter] Attempt ${attempt} did NOT improve "${params.chapterTitle}": got ${expandedWords} words (was ${currentWords}). Expansion may have been truncated.`
        );
        break;
      }

      console.log(
        `[expand-chapter] Attempt ${attempt} SUCCESS for "${params.chapterTitle}": ${currentWords} → ${expandedWords} words (${Math.round(expandedWords / adjustedTarget * 100)}% of target)`
      );
      currentContent = expanded;
    } catch (err: any) {
      console.error(
        `[expand-chapter] Attempt ${attempt} EXCEPTION for "${params.chapterTitle}":`,
        err?.message || err
      );
      console.error('[expand-chapter] Full error:', err);
      console.error('[expand-chapter] Stack trace:', err?.stack || 'no stack');

      // Network errors (TypeError: network error, fetch failures) are transient —
      // retry on the next attempt instead of bailing out entirely.
      if (attempt < MAX_EXPANSIONS && (err instanceof TypeError || err?.message?.includes('network'))) {
        console.log(
          `[expand-chapter] Network/transient error on attempt ${attempt} — will retry (attempt ${attempt + 1}/${MAX_EXPANSIONS})...`
        );
        await new Promise(resolve => setTimeout(resolve, 2000));
        continue;
      }
      break;
    }
  }

  // Log final word count status after all expansion attempts
  const finalWords = currentContent.trim().split(/\s+/).length;
  const finalPct = Math.round(finalWords / adjustedTarget * 100);
  if (finalWords < adjustedTarget * 0.80) {
    console.warn(
      `[expand-chapter] FINAL: "${params.chapterTitle}" is ${finalWords}/${adjustedTarget} words (${finalPct}%) — STILL UNDER 80% target after all expansion attempts`
    );
  } else {
    console.log(
      `[expand-chapter] FINAL: "${params.chapterTitle}" is ${finalWords}/${adjustedTarget} words (${finalPct}%)`
    );
  }

  return currentContent;
}

export async function grammarCheck(text: string): Promise<{ correctedText: string; corrections: any[] }> {
  const response = await apiFetch('/api/grammar-check', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });

  if (!response.ok) {
    console.error(`[grammar-check] Failed: HTTP ${response.status}`);
    return { correctedText: text, corrections: [] };
  }

  try {
    const data = await response.json();
    if (!data.correctedText || data.correctedText.trim().length < text.trim().length * 0.5) {
      console.warn('[grammar-check] Returned suspiciously short content — keeping original');
      return { correctedText: text, corrections: [] };
    }
    console.log(`[grammar-check] Applied ${data.corrections?.length || 0} corrections`);
    return data;
  } catch (err: any) {
    console.error('[grammar-check] Exception:', err?.message || err);
    return { correctedText: text, corrections: [] };
  }
}

export async function editorialPass(params: {
  text: string;
  bookTitle: string;
  chapterTitle: string;
  priorChapterSummaries?: string[];
}): Promise<string> {
  const response = await apiFetch('/api/editorial-pass', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    console.error(`[editorial-pass] Failed for "${params.chapterTitle}": HTTP ${response.status}`);
    return params.text;
  }

  try {
    const edited = await readSSEStream(response);
    if (!edited || edited.trim().length < params.text.trim().length * 0.5) {
      console.warn(`[editorial-pass] Returned suspiciously short content for "${params.chapterTitle}" — keeping original`);
      return params.text;
    }
    console.log(`[editorial-pass] Completed for "${params.chapterTitle}"`);
    return edited;
  } catch (err: any) {
    console.error(`[editorial-pass] Exception for "${params.chapterTitle}":`, err?.message || err);
    return params.text;
  }
}

export async function proofreadChapter(params: {
  content: string;
  bookTitle: string;
  chapterTitle: string;
  chapterType: string;
  allChapterTitles: string[];
  previouslyUsedNames: string[];
}): Promise<string> {
  const response = await apiFetch('/api/proofread-chapter', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    // Proofread is best-effort — if it fails, return the original content
    console.error(`[proofread] Failed for "${params.chapterTitle}": HTTP ${response.status}`);
    return params.content;
  }

  try {
    const proofread = await readSSEStream(response);
    if (!proofread || proofread.trim().length < params.content.trim().length * 0.5) {
      // Safety check: if proofread returned something suspiciously short, keep original
      console.warn(`[proofread] Returned suspiciously short content for "${params.chapterTitle}" — keeping original`);
      return params.content;
    }
    console.log(`[proofread] Completed for "${params.chapterTitle}"`);
    return proofread;
  } catch (err: any) {
    console.error(`[proofread] Exception for "${params.chapterTitle}":`, err?.message || err);
    return params.content;
  }
}

export async function generateOutline(params: {
  title: string;
  description: string;
  audience?: string;
  goals?: string;
  tone?: string;
  targetWordCount?: number;
  bookType?: string;
}): Promise<string> {
  const response = await apiFetch('/api/generate-outline', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to generate outline (${response.status})`);
  }

  const outline = await readSSEStream(response);

  if (!outline) {
    throw new Error('No outline was generated');
  }

  return outline;
}

export async function generateOutlineStreaming(
  params: {
    title: string;
    description: string;
    audience?: string;
    goals?: string;
    tone?: string;
    targetWordCount?: number;
    bookType?: string;
  },
  onChunk: (accumulated: string) => void
): Promise<string> {
  const response = await apiFetch('/api/generate-outline', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to generate outline (${response.status})`);
  }

  const outline = await readSSEStreamWithCallback(response, onChunk);

  if (!outline) {
    throw new Error('No outline was generated');
  }

  return outline;
}

export async function generateChapterBrief(params: {
  bookTitle: string;
  chapterTitle: string;
  chapterType: string;
  chapterNotes?: string;
  audience?: string;
  perspective?: string;
  goals?: string;
  target_word_count?: number;
  bookType?: string;
  researchContent?: string;
  priorChapterBriefs?: string[];
  citationsEnabled?: boolean;
  manualReferences?: string;
  chapterReferences?: string;
}): Promise<string> {
  const response = await apiFetch('/api/generate-chapter-brief', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to generate chapter brief (${response.status})`);
  }

  const brief = await readSSEStream(response);

  if (!brief) {
    throw new Error('No chapter brief was generated');
  }

  return brief;
}

export interface OutlineValidationResult {
  approved: boolean;
  overallMessage: string;
  chapterFeedback: Array<{
    title: string;
    status: 'ok' | 'warning' | 'needs-work';
    suggestion: string;
  }>;
}

export async function validateOutline(params: {
  chapters: Array<{ chapter_title: string; notes?: string; type?: string }>;
  targetWordCount: number;
  bookType: string;
}): Promise<OutlineValidationResult> {
  const response = await apiFetch('/api/validate-outline', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to validate outline (${response.status})`);
  }

  const rawText = await readSSEStream(response);

  if (!rawText) {
    throw new Error('No validation result returned');
  }

  // Extract JSON object from the response — Claude may wrap it in code fences or add text
  const jsonMatch = rawText.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Validation response did not contain valid JSON');
  }

  try {
    const parsed = JSON.parse(jsonMatch[0]);
    // Ensure required fields exist with safe defaults
    return {
      approved: typeof parsed.approved === 'boolean' ? parsed.approved : false,
      overallMessage: parsed.overallMessage || 'Validation completed.',
      chapterFeedback: Array.isArray(parsed.chapterFeedback) ? parsed.chapterFeedback : [],
    };
  } catch {
    throw new Error('Failed to parse validation result');
  }
}

export interface TopicViabilityResult {
  viable: boolean;
  reason: string;
  subtopics_found: number;
  suggested_adjustments: string;
}

export async function checkTopicViability(params: {
  title: string;
  description: string;
  audience?: string;
  targetWordCount?: number;
  bookType: string;
  chapterCount?: number;
}): Promise<TopicViabilityResult> {
  const response = await apiFetch('/api/check-topic-viability', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to check topic viability (${response.status})`);
  }

  const text = await response.text();

  if (!text) {
    throw new Error('No viability result returned');
  }

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Viability response did not contain valid JSON');
  }

  try {
    const parsed = JSON.parse(jsonMatch[0]);
    return {
      viable: typeof parsed.viable === 'boolean' ? parsed.viable : false,
      reason: parsed.reason || 'Could not assess topic viability.',
      subtopics_found: typeof parsed.subtopics_found === 'number' ? parsed.subtopics_found : 0,
      suggested_adjustments: parsed.suggested_adjustments || '',
    };
  } catch {
    throw new Error('Failed to parse viability result');
  }
}